import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';

import { InteractiveResponseCheckAnswer } from 'approot/activity-builder/components/interactive-response/shared/interactive-response-check-answer';
import { InteractiveClickOrDrag } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag';
import InteractiveDragDrop from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop';
import {
  CANCELLED,
  DragItem,
  INTERACTIVE_CANVAS_ID,
  INTERACTIVE_CONTAINER_ID,
  INTERACTIVE_DRAG_DROP,
  PLACED,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import { isEveryItemDropped } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.utils';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { useSortableScrollManager } from 'approot/shared/sortable/sortable.hooks';
import {
  InteractiveResponseHelpControls,
  InteractiveResponseStartAgainControls,
} from '../../shared/interactive-response-controls';
import { InteractiveResponseControlsBottom } from '../../shared/interactive-response-controls.style';
import {
  InteractiveResponseScreenReader,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  useInteractiveAudio,
  useInteractiveClickOrDrag,
} from '../../shared/interactive-response-shared.hooks';
import {
  InteractiveCanvas,
  InteractiveCanvasDragZone,
  InteractiveCanvasDragZoneOuter,
  InteractiveResponsePlayContainer,
  InteractiveResponseWrapper,
  InteractiveStackDragContainer,
} from '../../shared/interactive-response-shared.style';
import {
  isEverythingCorrect as checkIsEverythingCorrect,
  returnItemToOriginalPosition,
} from '../../shared/interactive-response-shared.utils';
import {
  DROP_ITEM_SIZE,
  InteractiveSequenceAttempt,
  RESPONSE_FREE,
  ViewProps,
} from '../settings/interactive-response.constants';
import { useInteractiveResponsePlay } from './interactive-response-sequence.hooks';
import * as Styled from './interactive-response-sequence.style';
import {
  initialise,
  populateAnswers,
  resetIncorrectItems,
} from './interactive-response-sequence.utils';
import { handleItemReturn } from '../../shared/interactive-response-shared.utils';
import { InteractiveSequenceDrop } from './interactive-sequence-drop';
import { useInteractiveAudioContext } from '../../shared/interactive-audio.context';
import InteractiveSequenceFree from './interactive-sequence-free';
import {
  InteractiveResponseSelectAreaSnackbar,
  InteractiveResponseSelectItemSnackbar,
} from '../../shared/interactive-response-snackbars';

type Props = ViewProps & {
  attemptAnswers?: InteractiveResponseAnswerFragment;
  onExpandImage: (id: string, dragItems: DragItem[]) => void;
  onAnswerAttempt: InteractiveSequenceAttempt;
  showAnswer: boolean;
};

const InteractiveResponseSequence = ({
  properties,
  attemptAnswers,
  onAnswerAttempt,
  onExpandImage,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(true);

  const sequenceProperties = properties.interactiveResponse;
  const isCheckAnswerAtEnd =
    sequenceProperties.checkAnswerAtEnd === null
      ? true
      : sequenceProperties.checkAnswerAtEnd;

  const { setAudioList } = useInteractiveAudioContext();

  const { toggleAudio, playClick, playStart, playAudio } =
    useInteractiveAudio();

  const {
    playAttempt,
    setPlayAttempt,
    dropItems,
    setDropItems,
    dragItems,
    setDragItems,
    keyboardState,
    handleOnDrop,
    handleKeyboardInteraction,
    handleCheckAnswers,
  } = useInteractiveResponsePlay({
    playClick,
    isCheckAnswerAtEnd,
    onAnswerAttempt,
  });

  const {
    dropZoneSelected,
    dragItemSelected,
    handleItemReturnToStack,
    handleDroppedDragItemClick,
    handleDragItemClick,
    handleDropZoneClick,
  } = useInteractiveClickOrDrag();

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      const { dragItems, dropItems } =
        attemptAnswers && !startAgain
          ? populateAnswers(
              attemptAnswers,
              sequenceProperties.items,
              sequenceProperties.variant
            )
          : initialise(
              sequenceProperties.variant,
              sequenceProperties.items,
              true
            );
      setDropItems(dropItems);
      setDragItems(dragItems);
      setIsTransitioning(true);
      setPlayAttempt(playAttempt + 1);
    },
    [
      sequenceProperties.items,
      sequenceProperties.variant,
      playAttempt,
      setPlayAttempt,
      setDragItems,
      setDropItems,
      setIsTransitioning,
      attemptAnswers,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      initialiseItems(startAgain);
      setTimeout(() => {
        setIsTransitioning(false);
        playStart();
      }, 600);
    },
    [initialiseItems, playStart]
  );

  const tryAgain = useCallback(() => {
    const { dragItems: updatedDragItems, dropItems: updatedDropItems } =
      resetIncorrectItems(dragItems, dropItems);

    setDragItems(updatedDragItems);
    setDropItems(updatedDropItems);
    setIsTransitioning(true);

    setTimeout(() => {
      setIsTransitioning(false);
      playStart();
    }, 600);
  }, [dragItems, dropItems, setDragItems, setDropItems, playStart]);

  const cancelKeyboardInteraction = useCallback(() => {
    if (
      keyboardState &&
      keyboardState.action !== CANCELLED &&
      keyboardState.action !== PLACED
    ) {
      handleKeyboardInteraction({
        action: CANCELLED,
        id: keyboardState.id,
        announce: 'You have cancelled keyboard interaction',
      });
    }
  }, [keyboardState, handleKeyboardInteraction]);

  const isAnswered = useMemo(() => {
    return isEveryItemDropped(dropItems);
  }, [dropItems]);

  const hasCheckedAnswer = useMemo(() => {
    return dropItems?.[0]?.zones?.[0]?.isCorrect !== undefined;
  }, [dropItems]);

  // checkIsEverythingCorrect() mutates the dropItems and dragItems so we only want to call it when we need to
  const isEverythingCorrect = useMemo(() => {
    if (isCheckAnswerAtEnd) {
      return hasCheckedAnswer
        ? checkIsEverythingCorrect(dropItems, dragItems)
        : false;
    }

    return isAnswered ? checkIsEverythingCorrect(dropItems, dragItems) : false;
  }, [dropItems, dragItems, isAnswered, isCheckAnswerAtEnd, hasCheckedAnswer]);

  const keyboardProps = {
    target:
      keyboardState?.action === CANCELLED || keyboardState?.action === PLACED
        ? undefined
        : keyboardState?.id,
    onKeyboardInteraction: handleKeyboardInteraction,
  };

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: INTERACTIVE_DRAG_DROP,
      canDrop(item, monitor) {
        const dragItem = dragItems.find(dragItem => dragItem.id === item.id);
        if (!dragItem?.isDropped) {
          return false;
        }
        return true;
      },
      drop: (item: any) => {
        const { updatedDragItems, updatedDropItems } = handleItemReturn(
          item,
          dragItems,
          dropItems
        );
        setDragItems(updatedDragItems);
        setDropItems(updatedDropItems);
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dragItems, dropItems]
  );

  const showDraggableItems = useMemo(() => {
    if (isCheckAnswerAtEnd) {
      return !hasCheckedAnswer;
    }

    // checking answers as we go so hide when everything is correct
    return !isEverythingCorrect;
  }, [isCheckAnswerAtEnd, isEverythingCorrect, hasCheckedAnswer]);

  useSortableScrollManager(ref, [INTERACTIVE_DRAG_DROP]);

  useEffect(() => {
    setup();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      sequenceProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, sequenceProperties.items]);

  return (
    <InteractiveResponseWrapper>
      <InteractiveResponsePlayContainer id={INTERACTIVE_CONTAINER_ID}>
        <InteractiveCanvas id={INTERACTIVE_CANVAS_ID}>
          <InteractiveResponseTitle
            title={sequenceProperties.title}
            subTitle={sequenceProperties.subTitle}
          />

          <InteractiveResponseCheckAnswer
            isEverythingAnswered={isAnswered}
            isAnswerChecked={hasCheckedAnswer}
            isCheckAnswerAtEnd={isCheckAnswerAtEnd}
            dragItems={dragItems}
            dropItems={dropItems}
            onStartOver={setup}
            onTryAgain={tryAgain}
            onCheckAnswer={handleCheckAnswers}
            moreInformation={sequenceProperties.moreInformation}
          />

          {showDraggableItems && (
            <InteractiveCanvasDragZoneOuter>
              <InteractiveCanvasDragZone
                ref={node => {
                  drop(node);
                }}
                $isOver={isOver && canDrop}
                $isDropZoneClickable={!!dropZoneSelected}
                role="button"
                data-drop-zone-clickable="true"
                $isHighlighted={
                  !!dragItemSelected && dragItemSelected.isDropped
                }
                onClick={() => {
                  if (dragItemSelected?.isDropped) {
                    handleItemReturnToStack({
                      dragItems,
                      dropItems,
                      setDragItems,
                      setDropItems,
                    });
                  }
                }}
              >
                {dragItems.map((dragItem, index) => (
                  <InteractiveStackDragContainer
                    id={dragItem.id}
                    key={`${playAttempt}-${dragItem.id}-${index}-${playAudio}`}
                    $index={index}
                    $isTransitioning={isTransitioning}
                    $isDropped={dragItem.isDropped}
                    data-selector={`sequence-image-${index}`}
                  >
                    {!dragItem.isDropped && (
                      <InteractiveClickOrDrag
                        id={dragItem.id}
                        imageUrl={dragItem.question.image?.small}
                        isFit={dragItem.question.image?.isFit}
                        description={dragItem.question.image?.description || ''}
                        baseColour={dragItem.question.baseColour}
                        variant={dragItem.question.variant}
                        text={dragItem.question.text}
                        richtext={dragItem.question.richtext}
                        audioUrl={dragItem.question.audioUrl}
                        keyboard={keyboardProps}
                        onExpandImage={() =>
                          onExpandImage(
                            dragItem.question.image?.itemId || '',
                            dragItems
                          )
                        }
                        isDropZoneActive={!!dropZoneSelected}
                        onClick={() =>
                          handleDragItemClick({
                            dragItem,
                            handleOnDrop,
                            dropItems,
                            cancelKeyboardInteraction,
                          })
                        }
                      />
                    )}
                  </InteractiveStackDragContainer>
                ))}
              </InteractiveCanvasDragZone>
            </InteractiveCanvasDragZoneOuter>
          )}

          <Styled.InteractiveResponseDropItemContainer>
            {dropItems.map(dropItem => {
              const zone = dropItem.zones[0];
              const droppedDragItem = dragItems.find(
                d => d.id === zone.dragItemId || d.id === zone.previewItemId
              );
              const isDisabled = zone.isCorrect !== undefined;

              return (
                <InteractiveSequenceDrop
                  key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                  id={dropItem.id}
                  label={dropItem.label}
                  labelLength={sequenceProperties.labelLength}
                  labelBorder={sequenceProperties.labelBorder}
                  isCorrect={zone.isCorrect}
                  dropItems={dropItems}
                  dragItems={dragItems}
                  onDrop={(destinationId, sourceId) => {
                    handleOnDrop(destinationId, sourceId);
                  }}
                  isContinuousMarking={!isCheckAnswerAtEnd}
                  onIncorrectAnswer={element =>
                    returnItemToOriginalPosition({
                      element,
                      droppedDragItemId: droppedDragItem?.id || '',
                      scale: 1,
                      dropItem,
                      dropItems,
                      dragItems,
                      setDragItems,
                      setDropItems,
                    })
                  }
                  isDropZoneSelected={dropZoneSelected === dropItem.id}
                  isDragItemSelected={!!dragItemSelected}
                  onDropZoneClicked={
                    isDisabled || droppedDragItem
                      ? undefined
                      : id =>
                          handleDropZoneClick({
                            handleOnDrop,
                            id,
                            cancelKeyboardInteraction,
                          })
                  }
                >
                  {droppedDragItem && (
                    <InteractiveClickOrDrag
                      id={droppedDragItem.id}
                      imageUrl={droppedDragItem.question.image?.small}
                      isFit={droppedDragItem.question.image?.isFit}
                      description={
                        droppedDragItem.question.image?.description || ''
                      }
                      baseColour={droppedDragItem.question.baseColour}
                      variant={droppedDragItem.question.variant}
                      text={droppedDragItem.question.text}
                      richtext={droppedDragItem.question.richtext}
                      audioUrl={droppedDragItem.question.audioUrl}
                      keyboard={keyboardProps}
                      isDisabled={isDisabled}
                      isCorrect={zone.isCorrect}
                      droppedAt={zone.dragItemId ? dropItem.label : ''}
                      overAt={zone.previewItemId ? dropItem.label : ''}
                      scaleTo={DROP_ITEM_SIZE}
                      isDropZoneActive={!!dropZoneSelected}
                      onClick={() => {
                        // disable clicks if the answer has been submitted
                        // or if checking answers immediately to match drag and drop behaviour
                        if (
                          (isCheckAnswerAtEnd && hasCheckedAnswer) ||
                          !isCheckAnswerAtEnd
                        ) {
                          return;
                        }

                        handleDroppedDragItemClick({
                          zone,
                          droppedDragItem,
                          dragItems,
                          dropItems,
                          setDragItems,
                          setDropItems,
                          handleOnDrop,
                          cancelKeyboardInteraction,
                        });
                      }}
                    />
                  )}
                </InteractiveSequenceDrop>
              );
            })}
          </Styled.InteractiveResponseDropItemContainer>

          <InteractiveResponseScreenReader keyboardState={keyboardState} />
        </InteractiveCanvas>
      </InteractiveResponsePlayContainer>

      <InteractiveResponseHelpControls
        howToPlay={sequenceProperties.howToPlay}
        toggleAudio={toggleAudio}
        playAudio={playAudio}
      />
      {isEverythingCorrect && (
        <InteractiveResponseControlsBottom>
          <InteractiveResponseStartAgainControls onClick={setup} />
        </InteractiveResponseControlsBottom>
      )}

      {dragItemSelected && <InteractiveResponseSelectAreaSnackbar />}
      {dropZoneSelected && <InteractiveResponseSelectItemSnackbar />}
    </InteractiveResponseWrapper>
  );
};

const InteractiveResponseSequenceWrapper = (props: Props) => {
  return (
    <InteractiveDragDrop>
      {props.properties.interactiveResponse.answerType === RESPONSE_FREE ||
      !props.showAnswer ? (
        <InteractiveSequenceFree {...props} />
      ) : (
        <InteractiveResponseSequence {...props} />
      )}
    </InteractiveDragDrop>
  );
};

export default InteractiveResponseSequenceWrapper;
